import { useCallback } from 'react';
import { SMSTask } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

const TASK_DELETE_EVENT = 'task_event_deleted';

const updateTasksArray = (existingTasks: SMSTask[], newTask: SMSTask, action: 'UPDATE' | 'DELETE') => {
  if (action === 'DELETE') {
    return existingTasks.filter((task) => task.taskId !== newTask.taskId);
  }

  const existingTask = existingTasks.find((task) => task.taskId === newTask.taskId);
  if (existingTask) {
    return existingTasks.map((task) => (task.taskId === newTask.taskId ? newTask : task));
  } else {
    return [...existingTasks, newTask];
  }
};

export const useSMSTaskWSHandler = () => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();

  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.SMS_TASK>>(
    async (event) => {
      const { params } = event;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!params.weaveNumber && !checkUserLocationPhoneAccess(params.weaveNumber)) {
        return;
      }

      const action = 'action' in params && params.action === TASK_DELETE_EVENT ? 'DELETE' : 'UPDATE';

      // Update the thread-level tasks list (updates the inbox list view).
      // This must be called before upsertThreadItem to match the original V4 call order,
      // where updateThreadsTasksList was always called first, independently of the item-level update.
      unifiedThreadUpdater.updateUnifiedThreadsTasksList({
        threadId: params.task.threadId,
        locationId: params.task.locationId,
        task: params.task,
        action,
      });

      // Update the individual thread item's tasks array in GetUnifiedThread cache
      // (updates the thread detail/conversation view).
      await unifiedThreadUpdater.upsertThreadItem({
        matchValues: {
          threadId: params.task.threadId,
          locationId: params.task.locationId,
          threadItemId: params.task.smsId,
        },
        updater: (threadItem) => {
          if (threadItem && 'smsMessage' in threadItem) {
            const sms = threadItem.smsMessage;
            return {
              smsMessage: {
                ...sms,
                tasks: updateTasksArray(sms.tasks ?? [], params.task, action),
              },
            };
          }
          return threadItem;
        },
        isOptimistic: false,
        getThreadOnly: true,
      });
    },
    [unifiedThreadUpdater]
  );

  return wsEventHandler;
};

export const useSMSTaskWSSubscription = () => {
  const handler = useSMSTaskWSHandler();
  return useWebsocketEventSubscription(NotificationType.SMS_TASK, handler);
};
